import React, { useCallback } from "react";
import styled from "styled-components";
import { Trans } from "@coworker/locales";
import { AmountPopupMultiPack } from "./AmountPopupMultiPack";
import SkapaTabs from "../SkapaTabs.jsx";
import { useWorkspacesState } from "../../../hooks/useWorkspaces";
import { StockInfoPillsWithRelatedTasks } from "../StocknfoPillsWithRelatedTasks";
import { useTasksForProduct } from "../../../hooks/useTasksForProduct";
import amountTypeOptions from "@coworker/enums/amountTypes";
import useFormatter from "../../../hooks/useFormatter";
import { useTranslation } from "@coworker/locales";
import Community from "../../../assets/svg/community.svg?react";
import { BlueLink } from "../../Link";
import { useParentPopup } from "../index";
import tracker from "../../../helpers/tracker";
import { useAvailableElevatedStock } from "../../../hooks/useAvailableElevatedStock";
import {
  useItemAmountSettings,
  useUpdateItemAmountSettings,
} from "@coworker/apprestructured/src/shared/hooks/item/useItemAmountSettings";
import { Container, OverflowBackground } from "../CommonComponents";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { SimpleToast } from "@coworker/apprestructured/src/shared/simple/SimpleToast/SimpleToast";

const { PALLET_SETTINGS, MULTI_PACK_SETTINGS, MULTI_PACK, PALLET, PIECES } =
  amountTypeOptions;

const isSettingsTab = (active) =>
  [MULTI_PACK_SETTINGS, PALLET_SETTINGS].includes(active);

const settingKeys = {
  [PALLET_SETTINGS]: "pieces_per_pallet",
  [MULTI_PACK_SETTINGS]: "pieces_per_multi_pack",
};

const InputWrapper = styled("div")`
  margin: 0 24px;
  margin-top: 12px;
`;

const ChangeTeamContainer = styled("div")`
  font-size: 14px;
  display: flex;
  align-items: center;
`;

const ChangeLink = styled((props) => <BlueLink {...props} />)`
  margin-top: 0px;
`;

const AssignedToContainer = styled("div")`
  margin-left: 10px;
  margin-right: 8px;
`;

const TeamChangeNearbyAddons = ({ name, onClick }) => (
  <ChangeTeamContainer data-testid="changeTeamContainer">
    <Community />
    <AssignedToContainer>
      <Trans values={{ name }}>assignedToWithNameString</Trans>
    </AssignedToContainer>
    <ChangeLink onClick={onClick}>
      <Trans>changeTeamString</Trans>
    </ChangeLink>
  </ChangeTeamContainer>
);

const Title = ({ i18nKey, count }) => {
  const { t } = useTranslation();
  return <Trans count={count || t("notSetString")}>{i18nKey}</Trans>;
};

export function AmountTabsWithPills({
  oldAmountType = "",
  oldActualAmount = "",
  maxAmount,
  onClose,
  changeAmount,
  onSubmit,
  initializeWithOldAmount = false,
  productId,
  productArticleType,
  navigateToTaskForm,
  showTeamChange,
  lastAssignedTeam,
  isFoodArticle,
}) {
  const { t } = useTranslation();
  const { getInput } = useParentPopup();
  const { productUnit } = useFormatter();
  const { formData } = useWorkspacesState().navigationState;
  const [assignedTo, setAssignedTo] = React.useState({
    assignedName: lastAssignedTeam?.name,
    assignedTeamId: lastAssignedTeam?.id,
  });

  const normalizedOldAmountType = (oldAmountType || "").toUpperCase(); // Just uppercasing is good enough if only "pieces" appear in lowercase in old task documents, which is true on -test.
  const [active, setActive] = React.useState(normalizedOldAmountType || PIECES);

  const [isSettingsSaved, setIsSettingsSaved] = React.useState(false);
  const [hasSettingsChanged, setHasSettingsChanged] = React.useState(false);
  const shortId = formData?.product_article_id || productId;
  const updateItemAmountSettings = useUpdateItemAmountSettings(shortId);

  const { data: amountSettingsForItem, isLoading: isLoadingAmountSettings } =
    useItemAmountSettings(shortId);

  const [amountSettings, setAmountSettings] = React.useState(
    amountSettingsForItem ?? {}
  );
  React.useEffect(() => {
    if (amountSettingsForItem && !isLoadingAmountSettings)
      setAmountSettings(amountSettingsForItem);
  }, [amountSettingsForItem, isLoadingAmountSettings]);

  const full_id = `${
    formData?.product_article_type || productArticleType
  }${shortId}`;

  const {
    available: availableStock,
    elevated,
    loading: loadingStockInfo,
    color,
  } = useAvailableElevatedStock(full_id);

  const isPackOrPallet = [MULTI_PACK, PALLET].includes(active);
  const getSetting = React.useCallback(
    (type) => {
      if (type === MULTI_PACK) {
        return amountSettings?.pieces_per_multi_pack?.toString(); //toString is required since tasks service will return number
      }
      if (type === PALLET) {
        return amountSettings?.pieces_per_pallet?.toString(); ////toString is required since tasks service will return number
      }
    },
    [amountSettings]
  );

  const hasSetting = (type) => !!getSetting(type);
  const actualAmount = initializeWithOldAmount ? oldActualAmount : "";

  /**
   * @param {*} type A type from amountTypeOptions
   * @returns {String} the amount to be displayed in the input
   */
  const getInputValue = useCallback(
    (type) => {
      if (type === MULTI_PACK_SETTINGS) {
        return getSetting(MULTI_PACK);
      } else if (type === PALLET_SETTINGS) {
        return getSetting(PALLET);
      } else if (type === normalizedOldAmountType && !hasSettingsChanged) {
        return String(actualAmount);
      } else return "";
    },
    [getSetting, normalizedOldAmountType, hasSettingsChanged, actualAmount]
  );

  const [valueAsString, setValue] = React.useState(getInputValue(active) || "");
  const [maxAmountExceededEvent, setMaxAmountExceededEvent] = React.useState({
    warningShown: false,
  });
  // This effect is used to update the SkapaInput field value. It should trigger when we switch selected tab.
  React.useEffect(
    () => {
      setValue(getInputValue(active));
    },
    // eslint-disable-next-line
    [active]
  );

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (isSettingsSaved) setIsSettingsSaved(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [isSettingsSaved]);

  const calculateTotalPieces = React.useCallback((amount, piecesPerPack) => {
    return Number(amount) * (piecesPerPack || 1);
  }, []);

  const getMaxPiecesText = () => {
    if (maxAmount) {
      const displayUnit = productUnit(PIECES);
      return (
        <Trans values={{ quantity: maxAmount, unit: displayUnit }}>
          maxNUnitsString
        </Trans>
      );
    }
  };

  const setHelperText = (type) => {
    if (type === MULTI_PACK) {
      return valueAsString && hasSetting(type) ? (
        <p>
          = {calculateTotalPieces(valueAsString, getSetting(type))}{" "}
          {t("piecesUnit")}
          {maxAmount ? " (" : ""}
          {getMaxPiecesText()}
          {maxAmount ? ")" : ""}
        </p>
      ) : hasSetting(type) ? (
        getMaxPiecesText()
      ) : (
        ""
      );
    } else if (type === PALLET) {
      return valueAsString && hasSetting(type) ? (
        <p>
          = {calculateTotalPieces(valueAsString, getSetting(type))}{" "}
          {t("piecesUnit")}
          {maxAmount ? " (" : ""}
          {getMaxPiecesText()}
          {maxAmount ? ")" : ""}
        </p>
      ) : hasSetting(type) ? (
        getMaxPiecesText()
      ) : (
        ""
      );
    } else if (type === PALLET_SETTINGS || type === MULTI_PACK_SETTINGS)
      return "";
    return getMaxPiecesText();
  };

  const goToSettings = React.useCallback(() => {
    setActive((active) =>
      active === MULTI_PACK ? MULTI_PACK_SETTINGS : PALLET_SETTINGS
    );
  }, []);

  const onComplete = React.useCallback(
    (value) => {
      if (isSettingsTab(active)) {
        const nextAmountSettings = {
          pieces_per_multi_pack: amountSettings?.pieces_per_multi_pack ?? "",
          pieces_per_pallet: amountSettings?.pieces_per_pallet ?? "",
          [settingKeys[active]]: value,
        };
        setAmountSettings(nextAmountSettings);
        setHasSettingsChanged(true);
        updateItemAmountSettings.mutateAsync(nextAmountSettings).then(() => {
          setIsSettingsSaved(true);
        });
      } else {
        const amount = Number(value);
        const totalPieces = isPackOrPallet
          ? calculateTotalPieces(amount, getSetting(active))
          : amount;
        onSubmit({
          amount: totalPieces,
          actualAmount: amount,
          amountType: active,
          assignedTeamId: assignedTo.assignedTeamId,
          assignedUserId: assignedTo.assignedUserId,
        });
      }
    },
    [
      updateItemAmountSettings,
      active,
      amountSettings,
      assignedTo.assignedTeamId,
      assignedTo.assignedUserId,
      calculateTotalPieces,
      getSetting,
      isPackOrPallet,
      onSubmit,
    ]
  );

  const getTabContent = (dataTestId) => {
    return (
      <AmountPopupMultiPack
        isFoodArticle={isFoodArticle}
        availableStock={availableStock}
        maxAmount={maxAmount}
        oldAmountSettings={amountSettings}
        goToSettings={goToSettings}
        activeTab={active}
        inputValue={valueAsString}
        helperText={setHelperText(active)}
        totalPieces={calculateTotalPieces(valueAsString, getSetting(active))}
        dataTestId={dataTestId}
        maxAmountExceeded={() => {
          maxAmountExceededEvent.warningShown = true;
        }}
        onComplete={onComplete}
        setValue={setValue}
      />
    );
  };

  const openAssignToPopUp = React.useCallback(async () => {
    const data = await getInput("selectAssignees", {
      title: <Trans>reassignString</Trans>,
      shortId,
      assignees: {
        gid: assignedTo.assignedTeamId,
        uid: assignedTo.assignedUserId,
      },
    });
    if (!data) return;
    setAssignedTo({
      assignedName: data.name,
      assignedTeamId: data.gid,
      assignedUserId: data.uid,
    });
  }, [assignedTo, getInput, shortId]);

  const relatedTasks = useTasksForProduct(shortId);

  const tabs = [
    {
      title: <Trans>piecesTitleString</Trans>,
      key: PIECES,
      testId: PIECES,
      content: getTabContent(PIECES),
    },
    {
      title: (
        <Title i18nKey="multipackTitleString" count={getSetting(MULTI_PACK)} />
      ),
      key: MULTI_PACK,
      testId: MULTI_PACK,
      content: getTabContent(MULTI_PACK),
    },
    {
      title: <Title i18nKey="palletTitleString" count={getSetting(PALLET)} />,
      key: PALLET,
      testId: PALLET,
      content: getTabContent(PALLET),
    },
    {
      title: <Trans>multipackSettingsTitleString</Trans>,
      key: MULTI_PACK_SETTINGS,
      testId: MULTI_PACK_SETTINGS,
      content: getTabContent(MULTI_PACK_SETTINGS),
    },
    {
      title: <Trans>palletSettingsTitleString</Trans>,
      key: PALLET_SETTINGS,
      testId: PALLET_SETTINGS,
      content: getTabContent(PALLET_SETTINGS),
    },
  ];

  React.useEffect(() => {
    setMaxAmountExceededEvent(maxAmountExceededEvent);

    // Here we use the unmount hook to send off a message to the tracker.
    // So the tracking happens when this screen is closed (or just garbage collected and then re-rendered, but that's uncommon enough for now to not be an issue)
    return () => {
      if (maxAmountExceededEvent.warningShown) {
        tracker.trackMaxAmountExceeded(maxAmountExceededEvent);
      }
    };
  }, [maxAmountExceededEvent]);

  const tabChangedHandler = (goodArgumentName) => {
    setActive(goodArgumentName);
  };

  return (
    <>
      <OverflowBackground
        data-testid="background"
        onClick={() => {
          tracker.trackDismissModal(false);
          onClose();
        }}
      />
      <Container data-testid="amountScreen">
        <FixaModalHeader
          title={t("piecesTitleString")}
          closeBtnClick={onClose}
        />
        <InputWrapper>
          <StockInfoPillsWithRelatedTasks
            relatedTasksCount={relatedTasks?.formattedCount}
            ofWhichClosed={relatedTasks?.ofWhichClosed}
            productFullId={full_id}
            productId={shortId}
            navigateToTaskForm={navigateToTaskForm}
            onClose={onClose}
            stockInfo={{
              available: availableStock,
              elevated,
              color,
              loading: loadingStockInfo,
            }}
          />
          <SkapaTabs
            tabs={tabs}
            active={active}
            onTabChanged={tabChangedHandler}
          />
          {isSettingsTab(active) && isSettingsSaved && (
            <SimpleToast
              text={t("savedForStoreString")}
              isOpen={isSettingsSaved}
              onCloseRequest={() => {
                setIsSettingsSaved(false);
              }}
              ariaLabel={t("savedForStoreString")}
            />
          )}
          {!!showTeamChange && (
            <TeamChangeNearbyAddons
              name={assignedTo.assignedName}
              onClick={openAssignToPopUp}
            />
          )}
        </InputWrapper>
      </Container>
    </>
  );
}
